// Collars After State (TMHQ, frontend-only). Tier-1 contracts.
//
// "After State" = the stored desired END-STATE positions of ONE leaf group (NOT deltas). It is a set of
// legs describing what the group should hold once the planned transaction is executed. Storage is user
// settings (one key per leaf group), NOT a database — see CollarsAfterStateService. The apply/preview diff
// re-derives Close/Open/Increase/Decrease from (current positions vs the saved end-state); the edit
// primitives below only shape the END-STATE.
//
// No new backend op for storage. Booking reuses the existing RecordCollarsTransaction (one per derived
// leg). Indicator is purely frontend-derived from the existence of a group's after-state settings key.

/** One leg of a group's desired end-state (what the group should hold). qty is a positive magnitude. */
export interface CollarsAfterStateLeg {
   // The leaf group the leg belongs to. One stored envelope (CollarsAfterState) covers exactly ONE group, so
   // persisted legs may omit this — the loader stamps it from the envelope's groupBucketId. In-memory legs
   // (diff result After column) always carry it so the preview/apply tables bucket each leg correctly.
   groupBucketId?: string;
   tradingInstrumentCode: string;
   kind: number;            // 0 = Asset, 1 = Option (matches CollarsPositionDto.kind / RecordCollarsTransaction.kind)
   side: number;            // 1 = Buy (long), -1 = Sell (short)
   qty: number;             // positive magnitude
   optionType?: string;     // 'Put' | 'Call' (option legs only)
   strike?: number;         // option legs only
   expiration?: any;        // option legs only (the descriptor/string the picker emits)
}

/** The persisted end-state for one leaf group, exactly as written to / read from user settings. */
export interface CollarsAfterState {
   groupBucketId: string;
   terminalId: string;
   legs: CollarsAfterStateLeg[];
   savedAtIso: string;      // UTC ISO instant the end-state was last saved
}

/**
 * A row in the Make-Transaction dialog's editable end-state table (HedgePad parity). It carries the edit
 * state (crossed-out, cloned-from, new) on top of the leg's values. The dialog edit primitives mutate
 * these rows ONLY; current positions are untouched until Apply/Direct-Commit re-derives the transaction.
 */
export interface CollarsEditableLeg {
   id: number;                 // dialog-local row id (++seq)
   isNew: boolean;             // added in the dialog (no matching current position)
   crossedOut: boolean;        // X = excluded from the end-state (existing => restorable, new => removed)
   restorable: boolean;        // true when crossing-out can be undone (existing legs); false for brand-new
   clonedFromId?: number;      // set on the clone half of a side-flip / strike-or-expiry change
   portfolioId?: string;
   comboId?: string;
   groupBucketId: string;      // the leaf group this leg belongs to
   kind: number;               // 0 = Asset, 1 = Option
   tradingInstrumentCode: string;
   side: number;               // 1 = Buy, -1 = Sell
   qty: number;                // edited end-state magnitude (positive; zero/negative BLOCKED by the dialog)
   originalQty?: number;       // the current live magnitude (for the dual ±increase/decrease input)
   targetQty?: number;         // the desired end-state magnitude the dual input resolves to
   optionType?: string;        // 'Put' | 'Call' (option legs only)
   strike?: number;            // option legs only
   expiration?: any;           // option legs only (existing leg: date string; new leg: OptionExpirationDescriptor)
   liveQuote?: number;         // last reference quote (display only; live via LastQuoteCacheService)
   quoteTicker?: string;       // the resolved ticker this leg is subscribed to for its live reference quote
}

/** One derived transaction leg (the diff between current and end-state for a single instrument). */
export interface CollarsTransactionDelta {
   groupBucketId: string;
   tradingInstrumentCode: string;
   kind: number;                                   // 0 = Asset, 1 = Option
   action: 'Open' | 'Close' | 'Increase' | 'Decrease';
   side: number;                                   // 1 = Buy, -1 = Sell (the side of the booking leg)
   qty: number;                                    // positive magnitude to trade
   strike?: number;
   optionType?: string;
   expiration?: any;
}

/** The derived transaction for one leaf group (its deltas). */
export interface CollarsGroupTransaction {
   groupBucketId: string;
   deltas: CollarsTransactionDelta[];
}

/** The three-column result the dialog Preview AND Apply both render (display only). */
export interface CollarsDiffResult {
   before: CollarsEditableLeg[];          // current live positions of the scope (face value)
   transaction: CollarsGroupTransaction[]; // derived deltas, grouped per leaf group
   after: CollarsAfterStateLeg[];         // the edited / saved end-state
   isValid: boolean;
   blockReason?: string;
}

/** Operator-supplied actual fill for one booking leg on Apply (one RecordCollarsTransaction per input). */
export interface CollarsActualFillInput {
   groupBucketId: string;
   tradingInstrumentCode: string;
   kind: number;            // 0 = Asset, 1 = Option
   side: number;            // 1 = Buy, -1 = Sell
   qty: number;             // positive magnitude
   actualAvgPx: number;     // operator-entered actual average fill price
   legOrder: number;        // sequencing hint (legs booked in ascending legOrder)
}

/** Make-Transaction dialog open config (scope = Portfolio / Combo / Group). */
export interface CollarsMakeTransactionConfig {
   scopeBucketId: string;
   level: number;           // 0 = Portfolio, 1 = Combo, 2 = Group
   terminalId: string;
}

export interface CollarsMakeTransactionResult {
   saved: boolean;          // end-state persisted to user settings
   committed: boolean;      // direct-committed via RecordCollarsTransaction now
}

/** Apply-After-State dialog open config (scope = Portfolio / Combo / Group). */
export interface CollarsApplyAfterStateConfig {
   scopeBucketId: string;
   level: number;           // 0 = Portfolio, 1 = Combo, 2 = Group
   terminalId: string;
}

export interface CollarsApplyAfterStateResult {
   applied: boolean;        // transaction booked + settings key(s) deleted
   // At least one bucket's saved plan was DISCARDED (X in the dialog) without applying. The panel must
   // refresh its green-indicator snapshot even when nothing was booked.
   discarded?: boolean;
}

/** User-settings key prefix for every Collars after-state entry (one per leaf group). */
export const COLLARS_AFTER_STATE_PREFIX = 'collars.after-state.';

/** The user-settings key for a single leaf group's after-state. */
export function collarsAfterStateKey(groupBucketId: string): string {
   return COLLARS_AFTER_STATE_PREFIX + groupBucketId;
}
